import React, { useState, useEffect, useCallback } from 'react'
import uniqBy from 'lodash/uniqBy'
import { validationSchema } from 'schema/share'
import { generateAllForPayload } from 'utils/helpers'
import { useAuth, useIntl, useConfirm, useGetFilesSummary } from 'hooks'
import { useGetSharedWithMutation, useShareMutation, useGetUsersQuery } from 'services'
import ShareDialogBox from 'components/Share/ShareDialogBox'
import ShareHeaderInfo from 'components/Share/ShareHeaderInfo'
import { FormContainer } from 'components/Form'
import Form from './Form'
import './index.css'

const SUMMARY_ICONS = {
    collection: 'folder',
    patient: 'user',
    files: 'folder',
    image: 'image',
    video: 'video',
}

const Share = ({ onClose, data }) => {
    const { t } = useIntl()
    const { getConfirmation } = useConfirm()
    const [create, setCreate] = useState(false)
    const { authUser, isAuthorized } = useAuth()
    const [getShareWithUsers, { data: sharedUsers }] = useGetSharedWithMutation()
    const [share] = useShareMutation()
    const filesCount = useGetFilesSummary(data)
    const { data: agreementUsers } = useGetUsersQuery(authUser.agreementId, {
        skip: !isAuthorized,
    })

    useEffect(() => {
        let payload = {}
        if (data.type === 'files') {
            payload.filesList = generateAllForPayload(data).fileIds
        } else {
            payload = generateAllForPayload(data)
        }
        getShareWithUsers(payload)
    }, [])

    const handleUnshare = async () => {
        const confirmed = await getConfirmation({
            title: t(`unshare.${data.type}.title`),
            message: t(`unshare.${data.type}.message`),
            buttons: [t('cancel'), t('unshare')],
        })
        if (!confirmed) return

        const payload = generateAllForPayload(data)
        payload.manageUserAction = uniqBy(
            sharedUsers.shardWith.map(e => {
                return {
                    shareeId: e.sharedTo,
                    actionFlg: 'D',
                }
            }),
            'shareeId'
        )

        share(payload)
        onClose()
    }

    const selfShared =
        sharedUsers?.shardWith.filter(e => e.sharedBy === authUser.emailId).map(e => e.sharedTo) ||
        []
    const sharedWithEmails = sharedUsers
        ? uniqBy(
              sharedUsers.shardWith.map(e => ({ email: e.sharedTo })),
              'email'
          ).map(e => (selfShared.includes(e.email) ? { ...e, canRevoke: true } : e))
        : []
    const hasSharedWith = sharedUsers?.shardWith.length > 0 && !sharedUsers?.isMixed

    const actionButtons = useCallback(() => {
        const actions = [
            {
                title: t('cancel'),
                onClick: onClose,
                ref: 'cancel',
            },
        ]

        if (data.type === 'collection' && sharedUsers?.shardWith.length) {
            actions.push({
                title: t('unshare.collection'),
                onClick: handleUnshare,
            })
        }

        if (data.type === 'patient' && sharedUsers?.shardWith.length) {
            actions.push({
                title: t('unshare.patient'),
                onClick: handleUnshare,
            })
        }

        if (data.type === 'files' && sharedUsers?.shardWith.length) {
            actions.push({
                title: t('unshare.files'),
                onClick: handleUnshare,
            })
        }

        if (sharedWithEmails.length > 0) {
            actions.push({
                title: t('update.access'),
                ref: 'submit',
            })
        } else {
            actions.push({
                title: t('give.access'),
                ref: 'submit',
            })
        }

        return actions
    }, [data, sharedWithEmails])

    const handleSubmit = values => {
        const emails = hasSharedWith ? values.sharedWith : values.emails

        const payload = {
            manageUserAction: emails
                .filter(e => e.flag)
                .map(e => {
                    return {
                        shareeId: e.email,
                        actionFlg: e.flag,
                    }
                }),
            ...generateAllForPayload(data),
            isAnonymized: values.isAnonymized,
            isDownloadable: values.isDownloadable,
        }

        if (data.type === 'collection') {
            payload.collectionName = data.name
        } else if (data.type === 'patient') {
            payload.patientData = {
                patientId: data.id,
                patientName: data.name,
            }
        } else if (data.type === 'files' && data.patient) {
            payload.patientData = {
                patientId: data.patient.id,
                patientName: data.patient.name,
            }
        } else if (data.type === 'files' && data.collection) {
            payload.collectionName = data.collection.name
        }

        if (payload.manageUserAction.length) {
            share(payload)
        }
        onClose()
    }

    const isShareFromCollection = data.type === 'collection' || data.collection

    return (
        <div className="share-wrap">
            <FormContainer
                initialValues={{
                    email: '',
                    emails: [],
                    sharedWith: sharedWithEmails,
                    isAnonymized: true,
                    isDownloadable: true,
                    agreementUsers,
                }}
                onSubmit={values => {
                    handleSubmit(values)
                }}
                validationSchema={create ? validationSchema(t) : undefined}
                validateOnChange={false}
                validateOnBlur={false}
                enableReinitialize
            >
                {formik => {
                    return (
                        <ShareDialogBox
                            headerText={t('share.title')}
                            onClose={onClose}
                            handleSubmit={formik.handleSubmit}
                            actions={actionButtons()}
                            setCreate={setCreate}
                        >
                            <ShareHeaderInfo
                                title={data.name}
                                icon={
                                    data.fileType
                                        ? SUMMARY_ICONS[data.fileType]
                                        : SUMMARY_ICONS[data.type]
                                }
                                count={data.type === 'files' ? null : filesCount}
                            />
                            <div className="patient-file-share-wrapper">
                                <Form
                                    {...formik}
                                    setCreate={setCreate}
                                    validationSchema={validationSchema}
                                    isShareFromCollection={isShareFromCollection}
                                    isMixed={sharedUsers?.isMixed}
                                    hasSharedWith={hasSharedWith}
                                />
                            </div>
                        </ShareDialogBox>
                    )
                }}
            </FormContainer>
        </div>
    )
}

export default Share
